import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, PackageCheck, Plus, Trash2 } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { newIdempotencyKey, todayIso } from '@/lib/utils';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, DatePicker, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { storeLabel, type PurchaseOrder, type StoreOption } from './InventoryPage';

const GOODS_RECEIVED_TAB = '/inventory?tab=goods-received';

interface GrnWarning {
  type: string;
  batchNo?: string;
  message: string;
}

interface GrnResult {
  warnings?: GrnWarning[];
}

let lineSequence = 0;
const nextLineKey = () => ++lineSequence;

interface GrnLine {
  key: number;
  productId: string;
  batchNo: string;
  expiryDate: string;
  qty: number;
  freeQty: number;
  purchasePrice: string;
  salePrice: string;
  taxPct: string;
}

interface ProductOption {
  id: string;
  code: string;
  genericName: string;
  strength: string | null;
  taxPct: number;
}

export function ReceiveGoodsPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), GOODS_RECEIVED_TAB);
  const initialStoreId = params.get('storeId') ?? '';
  const [storeId, setStoreId] = useState(initialStoreId);
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [lines, setLines] = useState<GrnLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey());
  const [warnings, setWarnings] = useState<GrnWarning[]>([]);
  const [purchaseOrderId, setPurchaseOrderId] = useState('');

  const stores = useQuery({
    queryKey: ['pharmacy-stores'],
    queryFn: () => get<StoreOption[]>('/pharmacy/stores'),
  });

  const suppliers = useQuery({
    queryKey: ['suppliers', 'active'],
    queryFn: () => get<{ id: string; name: string }[]>('/pharmacy/suppliers?isActive=true'),
  });

  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => get<ProductOption[]>('/pharmacy/products?limit=200'),
  });

  const orders = useQuery({
    queryKey: ['purchase-orders', 'open', supplierId],
    queryFn: () => get<PurchaseOrder[]>(`/pharmacy/purchase-orders${qs({ supplierId, limit: 100 })}`),
    enabled: Boolean(supplierId) && Boolean(storeId),
  });
  const openOrders = (orders.data ?? []).filter(
    (order) =>
      order.supplier.id === supplierId &&
      order.store.id === storeId &&
      (order.status === 'submitted' || order.status === 'partially_received'),
  );

  const chooseOrder = (id: string) => {
    setPurchaseOrderId(id);
    const order = openOrders.find((candidate) => candidate.id === id);
    if (!order) return;
    setLines(
      order.items
        .filter((item) => item.qty - item.receivedQty > 0)
        .map((item) => ({
          key: nextLineKey(),
          productId: item.productId,
          batchNo: '',
          expiryDate: todayIso(),
          qty: item.qty - item.receivedQty,
          freeQty: 0,
          purchasePrice: formatMoneyInput(item.unitCostMinor),
          salePrice: '0',
          taxPct: String(products.data?.find((product) => product.id === item.productId)?.taxPct ?? 0),
        })),
    );
  };

  const receive = useMutation({
    mutationFn: () =>
      post<GrnResult>(
        '/pharmacy/grns',
        {
          supplierId,
          storeId,
          purchaseOrderId: purchaseOrderId || undefined,
          supplierInvoiceNo: invoiceNo || undefined,
          lines: lines.map((line) => ({
            productId: line.productId,
            batchNo: line.batchNo,
            expiryDate: line.expiryDate,
            qty: line.qty,
            freeQty: line.freeQty,
            purchasePriceMinor: minorFromInput(line.purchasePrice),
            salePriceMinor: minorFromInput(line.salePrice),
            taxPct: Number(line.taxPct) || 0,
          })),
        },
        { idempotencyKey },
      ),
    onSuccess: (result) => {
      toast.success(tr('pharmacy', 'goods_received'), tr('pharmacy', 'stock_is_available_for_sale_immediately'));
      void queryClient.invalidateQueries({ queryKey: ['stock'] });
      void queryClient.invalidateQueries({ queryKey: ['grns'] });
      void queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['payables'] });
      setLines([]);
      if (result?.warnings?.length) {
        setWarnings(result.warnings);
        return;
      }
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const complete = lines.length > 0 && lines.every((l) => l.productId && l.batchNo && l.expiryDate && l.qty > 0);
  const dirty = !receive.isSuccess && (lines.length > 0 || storeId !== initialStoreId || supplierId !== '' || invoiceNo !== '');

  if (warnings.length > 0) {
    return (
      <FormScreen
        title={tr('pharmacy', 'goods_received')}
        description={tr('pharmacy', 'the_stock_is_in_check_these_before_the_invoice')}
        eyebrow={tr('pharmacy', 'inventory')}
        icon={PackageCheck}
        backTo={returnTo}
      >
        <FormSection columns={1}>
          <Alert variant="warning" title={`${warnings.length} thing${warnings.length === 1 ? '' : 's'} to check`}>
            <ul className="mt-1 list-disc space-y-1 ps-5">
              {warnings.map((warning, index) => (
                <li key={`${warning.type}-${warning.batchNo ?? index}`}>
                  {warning.batchNo ? `Batch ${warning.batchNo}: ` : ''}
                  {warning.message}
                </li>
              ))}
            </ul>
          </Alert>
          <div className="flex justify-end">
            <Button type="button" onClick={() => navigate(returnTo, { replace: true })}>
              {tr('pharmacy', 'done')}
            </Button>
          </div>
        </FormSection>
      </FormScreen>
    );
  }

  return (
    <FormScreen
      title={tr('pharmacy', 'receive_goods')}
      description={tr('pharmacy', 'batch_number_and_expiry_are_required_fefo')}
      eyebrow={tr('pharmacy', 'inventory')}
      icon={PackageCheck}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (!storeId || !supplierId || !complete) return;
        setError(null);
        receive.mutate();
      }}
    >
      <FormSection title={tr('pharmacy', 'goods_received')} icon={PackageCheck} columns={3}>
        <Field label={tr('pharmacy', 'store')} required>
          <Select
            value={storeId}
            onChange={(e) => {
              setStoreId(e.target.value);
              setPurchaseOrderId('');
            }}
          >
            <option value="">{tr('pharmacy', 'select')}</option>
            {stores.data?.map((store) => (
              <option key={store.id} value={store.id}>
                {storeLabel(store)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'supplier')} required hint={suppliers.data && suppliers.data.length === 0 ? tr('pharmacy', 'no_active_supplier_add_or_reactivate_one_on_the') : undefined}>
          <Select
            value={supplierId}
            onChange={(e) => {
              setSupplierId(e.target.value);
              setPurchaseOrderId('');
            }}
          >
            <option value="">{tr('pharmacy', 'select')}</option>
            {suppliers.data?.map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('pharmacy', 'supplier_invoice_no')}>
          <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
        </Field>
        {openOrders.length > 0 && (
          <Field label={tr('pharmacy', 'against_purchase_order')} hint={tr('pharmacy', 'fills_the_lines_with_what_is_still_to_come')} className="sm:col-span-2 lg:col-span-3">
            <Select value={purchaseOrderId} onChange={(e) => chooseOrder(e.target.value)}>
              <option value="">{tr('pharmacy', 'not_against_an_order')}</option>
              {openOrders.map((order) => (
                <option key={order.id} value={order.id}>
                  {order.number} — {order.items.length} {tr('pharmacy', 'line')}{order.items.length === 1 ? '' : 's'}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('pharmacy', 'items')} icon={ListChecks} columns={1}>
        <div className="flex items-center justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setLines((current) => [
                ...current,
                { key: nextLineKey(), productId: '', batchNo: '', expiryDate: todayIso(), qty: 1, freeQty: 0, purchasePrice: '0', salePrice: '0', taxPct: '0' },
              ])
            }
          >
            <Plus className="h-4 w-4" /> {tr('pharmacy', 'add_item')}
          </Button>
        </div>

        {lines.length > 0 && (
          <div className="hidden gap-2 px-2 text-xs font-medium text-muted-foreground sm:grid sm:grid-cols-12">
            <span className="sm:col-span-3">{tr('pharmacy', 'product')}</span>
            <span className="sm:col-span-2">{tr('pharmacy', 'batch_no')}</span>
            <span className="sm:col-span-2">{tr('pharmacy', 'expiry')}</span>
            <span className="sm:col-span-1">{tr('pharmacy', 'qty_2')}</span>
            <span className="sm:col-span-1">{tr('pharmacy', 'cost')}</span>
            <span className="sm:col-span-1">{tr('pharmacy', 'retail')}</span>
            <span className="sm:col-span-1">{tr('pharmacy', 'tax')}</span>
          </div>
        )}

        {lines.map((line, index) => (
          <div key={line.key} className="grid gap-2 rounded-md border p-2 sm:grid-cols-12">
            <Select
              className="sm:col-span-3"
              value={line.productId}
              onChange={(e) =>
                setLines((current) =>
                  current.map((l, i) =>
                    i === index
                      ? {
                          ...l,
                          productId: e.target.value,
                          taxPct: String(products.data?.find((product) => product.id === e.target.value)?.taxPct ?? 0),
                        }
                      : l,
                  ),
                )
              }
            >
              <option value="">{tr('pharmacy', 'select_a_product')}</option>
              {products.data?.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.code} — {product.genericName} {product.strength ?? ''}
                </option>
              ))}
            </Select>
            <Input
              className="sm:col-span-2"
              placeholder={tr('pharmacy', 'batch_no')}
              value={line.batchNo}
              onChange={(e) => setLines((c) => c.map((l, i) => (i === index ? { ...l, batchNo: e.target.value } : l)))}
            />
            <DatePicker
              className="sm:col-span-2"
              required
              value={line.expiryDate}
              onChange={(value) => setLines((c) => c.map((l, i) => (i === index ? { ...l, expiryDate: value } : l)))}
            />
            <Input
              className="sm:col-span-1 tabular"
              type="number"
              min={1}
              placeholder={tr('pharmacy', 'qty_2')}
              value={line.qty}
              onChange={(e) => setLines((c) => c.map((l, i) => (i === index ? { ...l, qty: Number(e.target.value) } : l)))}
            />
            <Input
              className="sm:col-span-1 tabular"
              type="text"
              dir="ltr"
              inputMode="decimal"
              placeholder={tr('pharmacy', 'cost')}
              value={line.purchasePrice}
              onChange={(e) => setLines((c) => c.map((l, i) => (i === index ? { ...l, purchasePrice: e.target.value } : l)))}
            />
            <Input
              className="sm:col-span-1 tabular"
              type="text"
              dir="ltr"
              inputMode="decimal"
              placeholder={tr('pharmacy', 'retail')}
              value={line.salePrice}
              onChange={(e) => setLines((c) => c.map((l, i) => (i === index ? { ...l, salePrice: e.target.value } : l)))}
            />
            <Input
              className="sm:col-span-1 tabular"
              type="number"
              min={0}
              max={100}
              step="0.01"
              placeholder={tr('pharmacy', 'tax')}
              value={line.taxPct}
              onChange={(e) => setLines((c) => c.map((l, i) => (i === index ? { ...l, taxPct: e.target.value } : l)))}
            />
            <Button
              type="button"
              className="sm:col-span-1"
              variant="ghost"
              size="icon"
              aria-label={tr('pharmacy', 'remove')}
              onClick={() => setLines((c) => c.filter((_, i) => i !== index))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('pharmacy', 'receive')} loading={receive.isPending} incomplete={!storeId || !supplierId || !complete} cancelTo={returnTo} />
    </FormScreen>
  );
}
